import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useFlow } from '../context/FlowContext';
import { useI18N } from '../context/I18NContext';
import { useSettings } from '../context/SettingsContext';
import useWidgetStore from '../store/useWidgetStore';
/**
 * How much room the bar takes off the scrolling content below it.
 *
 * The widget card is a fixed height and the bar is chrome, not content: whatever it occupies has
 * to come off the box that scrolls, or the bottom of the widget falls outside the card. BasicNav
 * subtracts this from that box's height, so keep it in step with the height set below.
 */
export const CONSUMER_BAR_HEIGHT = 40;
/**
 * The "Hallo <name>" bar, between the app bar and the view.
 *
 * Part of the chrome rather than of a view: it is shown on every route while someone is signed in,
 * it sits outside the scrolling box so it stays in sight the way the app bar does, and no view has
 * to know it exists.
 */
export default function ConsumerBar(props) {
    const theme = useTheme();
    const { t, ts } = useI18N();
    const navigate = useNavigate();
    const { hasSubscriptions, hasWaitinglists } = useSettings();
    const { booking, consumer, resetConsumer, setIsAccount, setIsDirtyAccount, setIsGuest, setIsLoginAccount, setIsNewAccount } = useWidgetStore();
    const flow = useFlow();
    // the menu hangs off the bar rather than off the button in it, so it lines up with the right
    // edge of the widget instead of with the end of the consumer's name
    const barRef = useRef(null);
    const [open, setOpen] = useState(false);
    // the name is what the consumer filled in, so any part of it can be missing; the e-mail is the
    // one thing a signed-in consumer always has
    const name = [consumer.firstName, consumer.insertions, consumer.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
        || consumer.email;
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };
    const goTo = (route) => {
        var _a, _b;
        handleClose();
        // the menu leaves the flow behind; remember the step it was opened from, so the back
        // arrow on the screen we are going to can put the consumer back where they were
        if (flow.insideFlow) {
            (_a = flow.setFlowReturn) === null || _a === void 0 ? void 0 : _a.call(flow, (_b = flow.currentRoute) === null || _b === void 0 ? void 0 : _b.route);
        }
        navigate(`/${route}`);
    };
    const logout = () => {
        var _a, _b;
        handleClose();
        // back to the state a visitor arrives in: no consumer, and the account flags at the
        // defaults in useWidgetStore. isDirtyAccount is part of that - it defaults to true and is
        // the requirement that puts accountDetails back into the flow for whoever signs in next.
        resetConsumer();
        setIsGuest(true);
        setIsAccount(false);
        setIsLoginAccount(false);
        setIsNewAccount(false);
        setIsDirtyAccount(true);
        if (((_b = (_a = booking === null || booking === void 0 ? void 0 : booking.appointments) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0) {
            // there is a booking in progress; it only lost the person it was for, so send them
            // back to identify themselves rather than throwing the appointment away
            navigate('/oa_widget_signIn');
        }
        else {
            // nothing in progress worth keeping, so start the flow from its first step
            if (flow.restart) {
                flow.restart();
            }
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, { ref: barRef, sx: {
                    height: `${CONSUMER_BAR_HEIGHT}px`,
                    // the border is part of the height BasicNav subtracts, not on top of it
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    // the trigger sits at the right edge, where the menu it opens lines up
                    justifyContent: 'flex-end',
                    paddingX: 1,
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    borderWidth: '0 0 1px 0',
                    borderStyle: 'solid'
                }, children: _jsxs(ButtonBase, { onClick: handleOpen, "aria-haspopup": "true", "aria-controls": open ? 'consumer-menu' : undefined, "aria-expanded": open ? 'true' : undefined, sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        paddingX: 1,
                        paddingY: '2px',
                        borderRadius: 1
                    }, children: [_jsx(Typography, { variant: "body2", sx: {
                                color: theme.palette.text.primary,
                                fontWeight: 500
                            }, children: ts('Welkom terug %name%', { name: name !== null && name !== void 0 ? name : '' }, 'account') }), _jsx(KeyboardArrowDownIcon, { fontSize: "small", sx: {
                                color: theme.palette.text.secondary,
                                transform: open ? 'rotate(180deg)' : 'none',
                                transition: 'transform 150ms'
                            } })] }) }), _jsxs(Menu, { id: "consumer-menu", anchorEl: barRef.current, open: open, onClose: handleClose, anchorOrigin: { horizontal: 'right', vertical: 'bottom' }, transformOrigin: { horizontal: 'right', vertical: 'top' }, children: [_jsxs(MenuItem, { onClick: () => goTo('oa_widget_accountDetails1'), children: [_jsx(ListItemIcon, { children: _jsx(PersonIcon, { fontSize: "small" }) }), t('Persoonlijke gegevens', 'account')] }), _jsxs(MenuItem, { onClick: () => goTo('oa_widget_appointments'), children: [_jsx(ListItemIcon, { children: _jsx(CalendarMonthIcon, { fontSize: "small" }) }), t('Afspraken', 'account')] }), hasSubscriptions && (_jsxs(MenuItem, { onClick: () => goTo('oa_widget_subscriptions'), children: [_jsx(ListItemIcon, { children: _jsx(CardMembershipIcon, { fontSize: "small" }) }), t('Strippenkaarten en abonnementen', 'account')] })), hasWaitinglists && (_jsxs(MenuItem, { onClick: () => goTo('oa_widget_waitinglists'), children: [_jsx(ListItemIcon, { children: _jsx(FormatListBulletedIcon, { fontSize: "small" }) }), t('Wachtlijsten', 'account')] })), _jsx(Divider, {}), _jsxs(MenuItem, { onClick: logout, children: [_jsx(ListItemIcon, { children: _jsx(LogoutIcon, { fontSize: "small" }) }), t('Uitloggen', 'account')] })] })] }));
}
